/**
 * Edit record — /m/$moduleKey/$entityKey/$recordId/edit
 *
 * Loads the entity definition, the saved layout, and the existing
 * record. Renders via <LayoutRenderer> in edit mode so the same node
 * tree that drives the detail view also drives the edit form. Field
 * nodes become input controls; computed nodes with `allowOverride`
 * render an editable override input.
 *
 * Falls back to the legacy `<EntityForm>` when the layout query
 * hasn't loaded yet, so the page is never blank.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import type { LayoutManifest } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { type LayoutFieldMeta, LayoutRenderer } from '@/components/custom/layout-renderer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/$entityKey/$recordId/edit')({
  component: EditRecordPage,
})

function EditRecordPage() {
  const { moduleKey, entityKey, recordId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: entity, isLoading: entityLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const { data: record, isLoading: recordLoading } = useQuery(
    orpc.modules.custom.records.get.queryOptions({
      input: { moduleKey, entityKey, recordId },
    }),
  )

  const { data: layoutData, isLoading: layoutLoading } = useQuery(
    orpc.extensions.layouts.get.queryOptions({
      input: { moduleKey, entityKey, kind: 'detail' },
    }),
  )

  const updateMutation = useMutation(
    orpc.modules.custom.records.update.mutationOptions({
      onSuccess: () => {
        toast.success('Record updated')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.get.queryKey({
            input: { moduleKey, entityKey, recordId },
          }),
        })
        void navigate({
          to: '/m/$moduleKey/$entityKey/$recordId',
          params: { moduleKey, entityKey, recordId },
        })
      },
      onError: (err) => toast.error(err.message || 'Failed to update'),
    }),
  )

  // Local form state — seeded from the loaded record once, then
  // mutated as the user edits. Computed-field keys are stripped on
  // initial seed since they're not part of the editable input shape;
  // override keys (`<field>_override`) ARE retained so the user can
  // clear or update an existing override.
  const [data, setData] = useState<Record<string, unknown>>({})
  const [seeded, setSeeded] = useState(false)

  useEffect(() => {
    if (record && entity && !seeded) {
      const computedKeys = new Set(
        (entity.computedFields as Array<{ key: string }>).map((cf) => cf.key),
      )
      const initial: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(record.data)) {
        if (!computedKeys.has(k) || k.endsWith('_override')) initial[k] = v
      }
      setData(initial)
      setSeeded(true)
    }
  }, [record, entity, seeded])

  if (entityLoading || recordLoading || layoutLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-3xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!entity || !record) {
    return (
      <div className="container mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold">Record not found</h1>
      </div>
    )
  }

  const fieldDefs = new Map<string, FieldDefinition>(
    (entity.fields as FieldDefinition[]).map((f) => [f.key, f]),
  )
  const coreFields = new Map<string, LayoutFieldMeta>(
    (entity.fields as FieldDefinition[]).map((f) => [
      f.key,
      {
        label: f.label,
        type: f.type,
        options: f.options,
        currency: f.currency,
        allowOverride: f.allowOverride,
      },
    ]),
  )

  const handleFieldChange = (fieldKey: string, value: unknown, isOverride?: boolean) => {
    setData((prev) => {
      const next = { ...prev }
      if (value === null && isOverride) {
        // Clearing an override: drop the key entirely so the backend
        // computes the auto value on the next read.
        delete next[fieldKey]
      } else {
        next[fieldKey] = value
      }
      return next
    })
  }

  const handleSubmit = async () => {
    await updateMutation.mutateAsync({
      moduleKey,
      entityKey,
      recordId,
      data,
    })
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-3xl space-y-4">
      <Link
        to="/m/$moduleKey/$entityKey/$recordId"
        params={{ moduleKey, entityKey, recordId }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to {entity.name}
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Edit {entity.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {layoutData ? (
            <LayoutRenderer
              layout={layoutData.layout as unknown as LayoutManifest}
              record={data}
              coreFields={coreFields}
              extensionFields={new Map()}
              scenarioColumns={
                (entity.scenarios as
                  | { columns?: Array<{ key: string; label?: string }> }
                  | null)?.columns ?? undefined
              }
              editMode
              fieldDefs={fieldDefs}
              onFieldChange={handleFieldChange}
            />
          ) : (
            <p className="text-sm text-muted-foreground italic">Loading layout…</p>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate({
                  to: '/m/$moduleKey/$entityKey/$recordId',
                  params: { moduleKey, entityKey, recordId },
                })
              }
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
